export type TokenType =
    | "IDENT"
    | "INT_LITERAL"
    | "LONG_LITERAL"
    | "DOUBLE_LITERAL"
    | "STRING_LITERAL"
    | "CHAR_LITERAL"
    | "KEYWORD"
    | "PUNCT"
    | "EOF";

export interface Token {
    type: TokenType;
    value: string;
    line: number;
}

const KEYWORDS = new Set([
    "public", "private", "protected", "static", "final", "class", "void",
    "int", "long", "double", "float", "boolean", "char", "String",
    "if", "else", "while", "do", "for", "return", "break", "continue",
    "true", "false", "new", "import",
]);

const PUNCTUATORS = [
    "<<=", ">>=",
    "==", "!=", "<=", ">=", "&&", "||", "++", "--",
    "+=", "-=", "*=", "/=", "%=",
    "{", "}", "(", ")", "[", "]", ";", ",", ".", ":", "?",
    "+", "-", "*", "/", "%", "=", "<", ">", "!", "&", "|",
];

export class LexError extends Error {
    line: number;

    constructor(message: string, line: number) {
        super(message);
        this.line = line;
    }
}

export function tokenize(source: string): Token[] {
    const tokens: Token[] = [];
    let i = 0;
    let line = 1;
    const n = source.length;

    const peek = (offset = 0) => source[i + offset];

    while (i < n) {
        const ch = source[i];

        if (ch === "\n") {
            line++;
            i++;
            continue;
        }

        if (ch === " " || ch === "\t" || ch === "\r") {
            i++;
            continue;
        }

        if (ch === "/" && peek(1) === "/") {
            while (i < n && source[i] !== "\n") i++;
            continue;
        }

        if (ch === "/" && peek(1) === "*") {
            i += 2;
            while (i < n && !(source[i] === "*" && peek(1) === "/")) {
                if (source[i] === "\n") line++;
                i++;
            }
            i += 2;
            continue;
        }

        if (/[A-Za-z_$]/.test(ch)) {
            let start = i;
            while (i < n && /[A-Za-z0-9_$]/.test(source[i])) i++;
            const word = source.slice(start, i);
            tokens.push({
                type: KEYWORDS.has(word) ? "KEYWORD" : "IDENT",
                value: word,
                line,
            });
            continue;
        }

        if (/[0-9]/.test(ch)) {
            let start = i;
            let isDouble = false;
            while (i < n && /[0-9]/.test(source[i])) i++;
            if (source[i] === "." && /[0-9]/.test(peek(1) ?? "")) {
                isDouble = true;
                i++;
                while (i < n && /[0-9]/.test(source[i])) i++;
            }
            let isLong = false;
            if (source[i] === "L" || source[i] === "l") {
                isLong = true;
                i++;
            } else if (source[i] === "D" || source[i] === "d" || source[i] === "F" || source[i] === "f") {
                isDouble = true;
                i++;
            }
            const text = source.slice(start, i).replace(/[LlDdFf]$/, "");
            tokens.push({
                type: isDouble ? "DOUBLE_LITERAL" : isLong ? "LONG_LITERAL" : "INT_LITERAL",
                value: text,
                line,
            });
            continue;
        }

        if (ch === '"') {
            i++;
            let out = "";
            while (i < n && source[i] !== '"') {
                if (source[i] === "\\") {
                    out += unescapeChar(source[i + 1]);
                    i += 2;
                } else {
                    out += source[i];
                    i++;
                }
            }
            if (i >= n) throw new LexError("Unterminated string literal", line);
            i++;
            tokens.push({type: "STRING_LITERAL", value: out, line});
            continue;
        }

        if (ch === "'") {
            i++;
            let value: string;
            if (source[i] === "\\") {
                value = unescapeChar(source[i + 1]);
                i += 2;
            } else {
                value = source[i];
                i++;
            }
            if (source[i] !== "'") throw new LexError("Unterminated char literal", line);
            i++;
            tokens.push({type: "CHAR_LITERAL", value, line});
            continue;
        }

        let matchedPunct: string | null = null;
        for (const p of PUNCTUATORS) {
            if (source.startsWith(p, i)) {
                matchedPunct = p;
                break;
            }
        }
        if (matchedPunct) {
            tokens.push({type: "PUNCT", value: matchedPunct, line});
            i += matchedPunct.length;
            continue;
        }

        throw new LexError(`Unexpected character '${ch}'`, line);
    }

    tokens.push({type: "EOF", value: "", line});
    return tokens;
}

function unescapeChar(c: string): string {
    switch (c) {
        case "n":
            return "\n";
        case "t":
            return "\t";
        case "r":
            return "\r";
        case "\\":
            return "\\";
        case '"':
            return '"';
        case "'":
            return "'";
        case "0":
            return "\0";
        default:
            return c;
    }
}
